import React from 'react';
import { Switch, Redirect } from 'react-router-dom';
import {
  ChangePasswordPage,
  Honeybadger,
  isSuperAdmin,
  ServiceWorkerEvents,
  useAuth,
  useAutoLogout,
  useNotifySWs,
  Toaster,
  withSuspense,
  useServiceWorker,
} from '@monument/core';
import { AdminRoute } from './AdminRoute/AdminRoute';

const lazy = callback => withSuspense(React.lazy(callback));

const DashboardRoute = lazy(() =>
  import('@routes/dashboard/DashboardRoute').then(module => ({
    default: module.DashboardRoute,
  })),
);
const NotFoundRoute = lazy(() =>
  import('@routes/not-found/NotFoundRoute').then(module => ({
    default: module.NotFoundRoute,
  })),
);
const PatientDetailsRoute = lazy(() =>
  import('@routes/patients/PatientDetailsRoute/PatientDetailsRoute').then(module => ({
    default: module.PatientDetailsRoute,
  })),
);
const CliniciansRoute = lazy(() =>
  import('@routes/clinicians/CliniciansRoute').then(module => ({
    default: module.CliniciansRoute,
  })),
);
const ClinicianDetailsRoute = lazy(() =>
  import('@routes/clinicians/ClinicianDetailsRoute/ClinicianDetailsRoute').then(module => ({
    default: module.ClinicianDetailsRoute,
  })),
);
const AdminsRoute = lazy(() =>
  import('@routes/admins/AdminsRoute').then(module => ({
    default: module.AdminsRoute,
  })),
);
const AdminDetailsRoute = lazy(() =>
  import('@routes/admins/AdminDetailsRoute/AdminDetailsRoute').then(module => ({
    default: module.AdminDetailsRoute,
  })),
);
const ChatRoute = lazy(() =>
  import('@routes/chat/ChatRoute').then(module => ({
    default: module.ChatRoute,
  })),
);
const ReviewsRoute = lazy(() =>
  import('@routes/reviews/ReviewsRoute').then(module => ({
    default: module.ReviewsRoute,
  })),
);
const AppointmentsRoute = lazy(() =>
  import('@routes/appointments/AppointmentsRoute').then(module => ({
    default: module.AppointmentsRoute,
  })),
);
const MessagePreviewsRoute = lazy(() =>
  import('@routes/message-previews/MessagePreviewsRoute').then(module => ({
    default: module.MessagePreviewsRoute,
  })),
);
const PayerContractsRoute = lazy(() =>
  import('@routes/payer-contracts/PayerContractsRoute').then(module => ({
    default: module.PayerContractsRoute,
  })),
);
const PayerContractDetailsRoute = lazy(() =>
  import('@routes/payer-contracts/PayerContractDetailsRoute/PayerContractDetailsRoute').then(
    module => ({
      default: module.PayerContractDetailsRoute,
    }),
  ),
);
const PayerFeeSchedulesRoute = lazy(() =>
  import('@routes/payer-fee-schedules/PayerFeeSchedulesRoute').then(module => ({
    default: module.PayerFeeSchedulesRoute,
  })),
);
const EligibilityRoute = lazy(() =>
  import('@routes/eligibility/EligibilityRoute').then(module => ({
    default: module.EligibilityRoute,
  })),
);
const InsuranceRoute = lazy(() =>
  import('@routes/insurance/InsuranceRoute').then(module => ({
    default: module.InsuranceRoute,
  })),
);
const EncountersRoute = lazy(() =>
  import('@routes/encounters/EncountersRoute').then(module => ({
    default: module.EncountersRoute,
  })),
);
const EncounterDetailsRoute = lazy(() =>
  import('@routes/encounters/EncounterDetailsRoute/EncounterDetailsRoute').then(module => ({
    default: module.EncounterDetailsRoute,
  })),
);

export const InitializeRouter = () => {
  const { user } = useAuth();
  useNotifySWs({ allowed: true, message: ServiceWorkerEvents.LOGIN });

  useServiceWorker();

  useAutoLogout({ isAdmin: true });

  return (
    <>
      <Honeybadger />
      <Switch>
        <AdminRoute path="/dashboard" component={DashboardRoute} />
        <AdminRoute
          path="/change-password"
          // TODO: change in future not the best solution
          component={() => <ChangePasswordPage className="px-[50px] py-[30px]" />}
        />
        <AdminRoute path="/not-found" component={NotFoundRoute} />
        <AdminRoute
          path="/admins/:nickname"
          component={isSuperAdmin(user) ? AdminDetailsRoute : () => <Redirect to="/dashboard" />}
        />
        <AdminRoute
          path="/admins"
          component={isSuperAdmin(user) ? AdminsRoute : () => <Redirect to="/dashboard" />}
        />
        <AdminRoute path="/patients/:nickname/:tabName?" component={PatientDetailsRoute} />
        <AdminRoute
          path="/patient/:nickname/:tabName?"
          component={args => {
            const { pathname, search } = args.location;
            const urlWithQueryParams = `${pathname.replace('patient', 'patients')}${search}`;
            return <Redirect to={urlWithQueryParams} />;
          }}
        />
        <AdminRoute path="/reviews/:page?" component={ReviewsRoute} />
        <AdminRoute path="/clinicians/:nickname/:name?" component={ClinicianDetailsRoute} />
        <AdminRoute path="/clinicians" component={CliniciansRoute} />
        <AdminRoute path="/chat/:tabName?/:channelId?" component={ChatRoute} />
        <AdminRoute path="/appointments" component={AppointmentsRoute} />
        <AdminRoute
          path="/message-previews/:tabName?/:template?"
          component={MessagePreviewsRoute}
        />
        <AdminRoute path="/insurance" component={InsuranceRoute} />
        <AdminRoute
          path="/payer-contracts/:payerContractId"
          component={PayerContractDetailsRoute}
        />
        <AdminRoute path="/payer-contracts" component={PayerContractsRoute} />
        <AdminRoute path="/payer-fee-schedules" component={PayerFeeSchedulesRoute} />
        <AdminRoute path="/eligibility" component={EligibilityRoute} />
        <AdminRoute path="/encounters/:encounterId" component={EncounterDetailsRoute} />
        <AdminRoute path="/encounters" component={EncountersRoute} />
        <Redirect exact from="/" to="/dashboard" />
        <AdminRoute path="*" component={() => <Redirect to="/not-found" />} />
      </Switch>
      <Toaster position="top-right" containerStyle={{ top: '76px' }} />
    </>
  );
};
